import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Tooltip } from './bootstrapJs';
import type { TooltipInstance } from './bootstrapJs';

import type { TooltipPlacement, TooltipTrigger } from './types';

export type NgTooltipProps = {
  children: ReactNode;
  text: string;
  placement?: TooltipPlacement;
  trigger?: TooltipTrigger;
  fadeOut?: boolean;
  milisecondsUntilFadeOut?: number;
};

export function NgTooltip({
  children,
  text,
  placement = 'right',
  trigger = 'click',
  fadeOut = true,
  milisecondsUntilFadeOut = 3000,
}: NgTooltipProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<TooltipInstance | null>(null);
  const fadeOutRef = useRef(fadeOut);
  const fadeOutMsRef = useRef(milisecondsUntilFadeOut);
  fadeOutRef.current = fadeOut;
  fadeOutMsRef.current = milisecondsUntilFadeOut;

  useEffect(() => {
    const element = spanRef.current;
    if (!element) {
      return;
    }

    const tooltip = new Tooltip(element, { sanitize: true });
    tooltipRef.current = tooltip;

    let hideTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleShown = () => {
      if (!fadeOutRef.current) {
        return;
      }
      hideTimeout = setTimeout(() => tooltip.hide(), fadeOutMsRef.current);
    };
    element.addEventListener('shown.bs.tooltip', handleShown);

    return () => {
      element.removeEventListener('shown.bs.tooltip', handleShown);
      if (hideTimeout !== undefined) {
        clearTimeout(hideTimeout);
      }
      tooltip.dispose();
      tooltipRef.current = null;
    };
  }, []);

  useEffect(() => {
    tooltipRef.current?.setContent({ '.tooltip-inner': text });
  }, [text]);

  return (
    <span
      ref={spanRef}
      className="d-inline-block"
      data-bs-container="body"
      data-bs-trigger={trigger}
      data-bs-placement={placement}
      data-bs-title={text}
      data-bs-toggle="tooltip"
    >
      {children}
    </span>
  );
}
